import { memo, useContext, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Moment from 'react-moment'
import { withRouter } from 'react-router'
import styled, { css } from 'styled-components'
import { Avatar } from '@/components/avatar'
import { Lock, ChatFavourite } from '@/components/icons'
import VerifiedBadge from '@/components/verified-badge'
import { ChatContext } from '@/features/Chat/chat-side-bar/chat-context'
import { BulkContext } from '@/pages/Chat/context'
import { breakpoints, colors } from '@/styles'
import capitalizeSentence from '@/utils/capitalize-sentence'
import useRole from '@/hooks/use-role'
import { Label } from '@/components/text'
import useMediaQuery from '@/hooks/use-media-query'
import { Input } from '@/components/form'
import useSearchParams, { stringifySearchParams } from '@/hooks/use-search-params'
import useIsMobile from '@/hooks/use-is-mobile'
import MenuDropdown from './menu-dropdown'

const WrapperOuter = styled.div`
  position: relative;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  height: 96px;
  align-items: center;
  padding: 16px 16px 16px 24px;
  cursor: pointer;
  border-bottom: 1px solid ${colors.grey88};
  background-color: ${colors.white};
  left: 0;
  z-index: 2;
  width: 100%;

  &.dragLeft {
    animation-duration: 0.2s;
    animation-name: slideout;
    animation-fill-mode: forwards;
  }

  &.dragRight {
    animation-duration: 0.2s;
    animation-name: slidein;
    animation-fill-mode: forwards;
  }

  @keyframes slidein {
    from {
      left: -64px;
    }
    to {
      left: 0;
    }
  }

  @keyframes slideout {
    from {
      left: 0;
    }
    to {
      left: -64px;
    }
  }

  ${props =>
    props.isSelected &&
    `
    background-color: #e9e9e9;
  `}

  ${props =>
    props.isUnread &&
    `
    background-color: ${colors.blue96};
  `}

  @media screen and (min-width: ${breakpoints.ipadMiniMax}) {
    :hover {
      background-color: ${colors.grey80};
    }
    ${props =>
      props.isMultiSelectOpen &&
      `
      background-color: ${colors.grey80};
    `}
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding-left: 25px;
  }
`

const ActionsContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  width: 64px;
  background-color: #3d3d3d;
  height: 96px;
  z-index: 1;
  align-items: center;
`

const ActionText = styled.div`
  color: ${colors.white};
  font-size: 12px;
  line-height: 16px;
  margin-top: 30px;
`

const AvatarContainer = styled.div`
  width: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  position: relative;
`

const ChatInfoContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: flex-start;
  padding-left: 16px;
  height: 100%;
  width: 100%;
  color: ${colors.black};
`

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 185px;
`

const overflowStyle = css`
  max-width: 175px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const boldStyle = css`
  ${props =>
    props.isBold &&
    `
    font-weight: 700;
  `}
`

const NameText = styled(Label)`
  ${overflowStyle}
  ${boldStyle}
`

const BuildingText = styled(Label)`
  color: ${colors.grey64};
  margin: 4px 0 8px;
  line-height: 16px;
  font-weight: 400;
  ${overflowStyle}
`

const MessageText = styled(Label)`
  font-weight: 400;
  ${overflowStyle}
  ${boldStyle}
`

const TimeStampContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: stretch;
  flex: 0 1 90px;
  align-items: flex-end;
  ${props => (props.selectMode ? 'justify-content: center' : '')}
`

const TimeStampText = styled(Label)`
  color: ${colors.grey48};
  line-height: 16px;
  font-weight: 400;
  ${props =>
    props.isBold &&
    `
    color: ${colors.black};
    font-weight: 700;
  `}
`

const CheckBox = styled(Input)``

const StatusTag = styled(Label)`
  font-size: 10px;
  line-height: 12px;
  text-align: center;
  border-radius: 8px;
  padding: 2px 4px;
`

const UpcomingTag = styled(StatusTag)`
  color: ${colors.green48};
  background-color: ${colors.green96};
`

const AppliedTag = styled(StatusTag)`
  color: ${colors.red64};
  background-color: ${colors.red96};
`

const PastTag = styled(StatusTag)`
  color: ${colors.grey40};
  background-color: ${colors.grey96};
`

const LandlordTenantTag = styled(StatusTag)`
  color: ${colors.blue48};
  background-color: ${colors.blue96};
`

const IconsWrapper = styled.div`
  display: flex;
  margin-bottom: 2px;
  gap: 8px;
  height: 14px;

  svg {
    flex-shrink: 0;
    height: 100%;
  }
`

const TimelineTag = ({ hasApplied, stateMachine, listingStateMachine }) => {
  const isLandlord = useRole() === 'landlord'
  const { t } = useTranslation()
  if (stateMachine === 'locked') return null
  if (listingStateMachine === 'upcoming')
    return <UpcomingTag>{t('b.chat.view.upcoming.text', 'Upcoming')}</UpcomingTag>
  if (hasApplied && listingStateMachine !== 'history' && listingStateMachine !== 'rented')
    return <AppliedTag>{t('b.chat.view.applied.text', 'Applied')}</AppliedTag>
  if (!isLandlord && stateMachine === 'rented' && listingStateMachine === 'rented')
    return <LandlordTenantTag>{t('common.landlord', 'Landlord')}</LandlordTenantTag>
  if (isLandlord && stateMachine === 'rented' && listingStateMachine === 'rented')
    return <LandlordTenantTag>{t('r.chat.view.tenant.text', 'Tenant')}</LandlordTenantTag>
  if (isLandlord && listingStateMachine === 'history')
    return <PastTag>{t('l.chat.view.past.text', 'Past')}</PastTag>
  return null
}

const dragLeft = 'dragLeft'
const dragRight = 'dragRight'

const ChatCard = ({
  address,
  avatar,
  hasApplied,
  history,
  stateMachine,
  listingStateMachine,
  last_event,
  created_at,
  last_event_message,
  is_read,
  match,
  name,
  timeline_room,
  isIdVerified,
  isGroupChat,
  highlight = false,
  isNameResult = false,
  targetMessageId,
  is_favourite
}) => {
  const { t } = useTranslation()
  const { getPath } = useContext(ChatContext)
  const timelineId = match?.params?.timelineId
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false)
  const pathObj = getPath(timeline_room)
  const fromPath = pathObj.replace(`/chat/${timeline_room}`, '/chat')
  const isUnread = is_read === '0'
  const isSelected = timeline_room === timelineId
  const isSupportRoom = timeline_room === 'support-room'
  const isLocked = stateMachine === 'locked'
  const params = useSearchParams()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const node = useRef()
  const bulkContext = useContext(BulkContext)
  const userAgentMobile = useIsMobile()

  const {
    currentNodeDragged,
    setCurrentNodeDragged,
    selectMode,
    setSelectMode,
    selectedChat,
    setSelectedChat,
    maxCount,
    setIsCheckAll,
    setNavigationState
  } = bulkContext
  const maxSelectReached = selectedChat.length >= maxCount

  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const [dragged, setDragged] = useState('')

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50

  const onTouchStart = e => {
    setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = e => setTouchEnd(e.targetTouches[0].clientX)

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !isMobile) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance
    if (isLeftSwipe || isRightSwipe) isLeftSwipe ? setDragged(dragLeft) : setDragged(dragRight)
  }

  const calendarStrings = {
    lastDay: `[${t('common.yesterday', 'Yesterday')}]`,
    sameDay: 'h:mm A',
    lastWeek: 'MMM DD',
    sameElse: 'MMM DD'
  }

  useEffect(() => {
    if (selectMode && dragged === dragLeft) setDragged(dragRight)
  }, [selectMode])

  useEffect(() => {
    // If a node has just been dragged left, set it to be the currentNodeDragged
    if (dragged === dragLeft && currentNodeDragged !== node) {
      setCurrentNodeDragged(node)
    }

    // If the currentNodeDragged has just been dragged right, nullify the currentNodeDragged
    if (dragged === dragRight && currentNodeDragged === node) {
      setCurrentNodeDragged(null)
    }
  }, [dragged])

  useEffect(() => {
    // Whenever currentNodeDragged changes, set all other nodes to be dragged right
    if (currentNodeDragged !== node && dragged === dragLeft) {
      setDragged(dragRight)
    }
  }, [currentNodeDragged])

  return (
    <WrapperOuter>
      <Wrapper
        ref={node}
        className={isMobile ? dragged : ''}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseOver={() => {
          if (!userAgentMobile) setIsMultiSelectOpen(true)
        }}
        onMouseLeave={() => setIsMultiSelectOpen(false)}
        isSelected={isSelected}
        isUnread={isUnread}
        onClick={event => {
          setNavigationState(true)
          // Checking for classname for event is hacky. TODO: refactor
          const { checked, classList } = event.target
          const MuiClass = 'MuiBackdrop-root'
          setSelectedChat([...selectedChat, timeline_room])
          if (!checked) {
            setSelectedChat(selectedChat.filter(item => item !== timeline_room))
          }
          if (selectMode || classList.contains(MuiClass)) return
          setSelectMode(false)

          const isSearchResult = !!params.search

          const method = isSearchResult ? 'replace' : 'push'
          history[method]({
            pathname: pathObj,
            state: {
              from: fromPath,
              isSearchResult,
              targetMessageId
            },
            search: stringifySearchParams(params)
          })
        }}
        isMultiSelectOpen={isMultiSelectOpen}
      >
        <AvatarContainer>
          <Avatar
            location={avatar}
            width={40}
            height={40}
            shadow={!isSupportRoom && !isGroupChat}
          />
          <TimelineTag
            hasApplied={hasApplied}
            stateMachine={stateMachine}
            listingStateMachine={listingStateMachine}
          />
        </AvatarContainer>
        <ChatInfoContainer>
          <NameWrapper>
            <NameText fontSize="16px" isBold={isUnread}>
              {name}
            </NameText>
            <VerifiedBadge verified={isIdVerified} type="id" width={20} height={20} />
          </NameWrapper>
          <BuildingText fontSize="12px">{address}</BuildingText>
          {highlight ? (
            <MessageText
              fontSize="12px"
              dangerouslySetInnerHTML={{ __html: highlight }}
              isBold={isUnread && !isNameResult}
            ></MessageText>
          ) : (
            <MessageText fontSize="12px" isBold={isUnread}>
              {capitalizeSentence(last_event_message?.message || last_event_message)}
            </MessageText>
          )}
        </ChatInfoContainer>
        {(last_event || created_at) && (
          <TimeStampContainer selectMode={selectMode}>
            {selectMode ? (
              <CheckBox
                onChange={e => {
                  e.stopPropagation()
                  if (!e.target.checked) setIsCheckAll(false)
                }}
                type="checkbox"
                id={timeline_room}
                disabled={selectedChat.includes(timeline_room) ? false : maxSelectReached}
                checked={selectedChat.includes(timeline_room)}
              />
            ) : (
              <>
                <TimeStampText fontSize="12px" isBold={isUnread}>
                  <Moment calendar={calendarStrings} local>{`${
                    created_at ? created_at : last_event
                  }-0000`}</Moment>
                </TimeStampText>
                <IconsWrapper>
                  {is_favourite === '1' && <ChatFavourite />}
                  {isLocked && <Lock />}
                </IconsWrapper>
                {isMultiSelectOpen && (
                  <MenuDropdown
                    name={name}
                    target={'desktop'}
                    timelineRoom={timeline_room}
                    is_favourite={is_favourite}
                    setSelectMode={setSelectMode}
                    setIsMultiSelectOpen={setIsMultiSelectOpen}
                  />
                )}
              </>
            )}
          </TimeStampContainer>
        )}
      </Wrapper>
      <ActionsContainer>
        <MenuDropdown
          name={name}
          target={'mobile'}
          timelineRoom={timeline_room}
          is_favourite={is_favourite}
          dragged={dragged}
          setDragged={setDragged}
          setSelectMode={setSelectMode}
          setIsMultiSelectOpen={setIsMultiSelectOpen}
        />
        <ActionText>{t('b.chat.view.bulk_edit.mark_as_read.actions', 'Actions')}</ActionText>
      </ActionsContainer>
    </WrapperOuter>
  )
}

ChatCard.defaultProps = {
  timeline: {},
  url: '',
  name: '',
  unit: '',
  last_read: '',
  last_event: '',
  is_read: '',
  isIdVerified: false
}

export default withRouter(memo(ChatCard))
